import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, Grid } from "@mui/material";
import { Box, Button, TextField, CheckboxField, Typography, DateTimeStandard } from "@curves/ui-base";
import { ProductInfo, GradientColorHeader, ReadTextField } from '@curves/ui-shared';
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import { callRef, isFunction, refIsRequiredError } from "@util/utils";
import { getNowTime, getToday, parseDate } from "@util/moment";
import useClubInventoryApi from "@apis/useClubInventoryApi";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";

// 功能：CheckboxField 結果轉成 boolean（僅取第一個 checkbox）
const toChecked = result => (Array.isArray(result) && result[0] ? !!result[0].checked : false);

// 功能：瑕疵回報 Dialog（商品資訊由庫存明細 API 單一來源提供）
const ConvertToDefectDialog = ({ open, onClose, sourceData = null, refresh, onSyncReturnFlow }) => {
	// 功能：表單欄位 ref（建單人、瑕疵狀況說明、同步執行退貨流程）
	const creatorRef = useRef(null);
	const descriptionRef = useRef(null);
	const isSyncReturnFlowRef = useRef(null);
	const { employeeOption } = useGetEmployeeOptionState();

	// 功能：商品庫存明細（由 API 取得後填入，用於左側商品顯示）
	const [detail, setDetail] = useState(null);

	const { getClubInventoryDetailApi, postClubInventoryDefectiveReportApi } = useClubInventoryApi();
	const productID = sourceData?.productID != null ? Number(sourceData.productID) : null;
	const variantID = sourceData?.variantID != null ? Number(sourceData.variantID) : 0;
	const reportDateText = parseDate(getToday(), DateTimeStandard.DisplayDate);

	// 功能：Dialog 開啟且具 productID 時，取得商品庫存明細；關閉或 productID 清空時重置 detail
	useEffect(() => {
		let isCancelled = false;

		const fetchDetail = async () => {
			if (!open || productID == null) {
				setDetail(null);
				return;
			}
			try {
				const resp = await getClubInventoryDetailApi({ productID, variantID });
				if (isCancelled) return;
				setDetail(resp ?? null);
			} catch (e) {
				if (!isCancelled) setDetail(null);
			}
		};

		fetchDetail();
		return () => {
			isCancelled = true;
		};
	}, [open, productID, variantID, getClubInventoryDetailApi]);

	// 功能：左側商品顯示資料（與 AdjustStockQtyDialog 一致）
	const productInfo = detail
		? {
				sku: detail.sku ?? "",
				name: detail.productName ?? "",
				spec: detail.variantName ?? "",
				imageUrl: detail.pictureSrc ?? "",
			}
		: null;

	// 功能：關閉時呼叫 onClose
	const handleClose = e => isFunction(onClose) && onClose(e);

	// 功能：驗證必填（商品、表單欄位）→ 呼叫新增瑕疵回報 API → 成功則 refresh 並關閉
	const handleSubmit = async e => {
		e.preventDefault();
		if (productID == null || !detail) return;

		const requiredRefs = [creatorRef, descriptionRef];
		if (refIsRequiredError(...requiredRefs)) return;

		const payload = {
			productID,
			variantID,
			reportDate: getNowTime(),
			employeeID: Number(callRef(creatorRef, "getResult")),
			reportReason: String(callRef(descriptionRef, "getResult") || "").trim(),
			defectiveImagePath: null,
			isSyncReturnFlow: toChecked(callRef(isSyncReturnFlowRef, "getResult")),
		};

		const resp = await postClubInventoryDefectiveReportApi(payload);
		if (resp) {
			if (payload.isSyncReturnFlow && isFunction(onSyncReturnFlow)) {
				onSyncReturnFlow();
			} else {
				if (isFunction(refresh)) refresh();
			}
			handleClose();
		}
	};

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-65rem" }} open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<GradientColorHeader onClose={handleClose}>瑕疵回報</GradientColorHeader>
			<DialogContent className="p-3">
				{productID == null ? null : !detail ? (
					<Typography color="textSecondary">無法取得庫存明細</Typography>
				) : (
					<Box component="form" onSubmit={handleSubmit} className="convert-to-defect-form">
						<Grid container spacing={3} alignItems="flex-start">
							<Grid xs={4} item className="convert-to-defect-form-product">
								<ProductInfo product={productInfo} layout="column" imageSize="auto" />
							</Grid>
							<Grid xs={8} item className="convert-to-defect-form-fields">
								<Grid container spacing={2}>
									<Grid item xs={6}>
										<ReadTextField label="建單日期">{reportDateText}</ReadTextField>
									</Grid>
									<Grid item xs={6}>
										<EmployeeSelect label="建單人" ref={creatorRef} options={employeeOption} fullWidth />
									</Grid>
									<Grid item xs={12}>
										<TextField label="原因" ref={descriptionRef} required placeholder="請說明商品瑕疵原因情況" multiline minRows={3} fullWidth />
									</Grid>
									<Grid item xs={12} className="mt-2">
										<CheckboxField ref={isSyncReturnFlowRef}>
											<CheckboxField.Checkbox label="同步執行退貨流程" value={true} />
										</CheckboxField>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Box className="mt-3 text-right">
							<Button type="submit" variant="contained">
								確認
							</Button>
						</Box>
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};

ConvertToDefectDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	sourceData: PropTypes.shape({
		productID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		variantID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	}),
	refresh: PropTypes.func,
	onSyncReturnFlow: PropTypes.func,
};

export default ConvertToDefectDialog;
